import { selectItemsInActiveCart } from 'machines/cart/selectors'
import { getCanRedeemLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { isLuxPlusSubscriptionItem, isLEHotelItem, isBedbankItem } from 'lib/checkout/checkoutUtils'
import { getHasLuxPlusOrSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscriptionItems'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import type { MultiCartSnapshot } from 'machines/cart/selectors'

/**
 * Snapshot-based utilities for multi-cart state. Use these in non-React contexts
 * (analytics, utilities) where hooks cannot be used.
 */

export function getItems(snapshot: MultiCartSnapshot): Array<App.Checkout.AnyItem> {
  const allItems = selectItemsInActiveCart(snapshot)
  return allItems.map((multiCartItem: App.MultiCartItem) => multiCartItem.item)
}

export function getAvailableItems(snapshot: MultiCartSnapshot): Array<App.Checkout.AnyItem> {
  const allItems = selectItemsInActiveCart(snapshot)
  return allItems
    .filter((multiCartItem: App.MultiCartItem) =>
      multiCartItem.state === 'active' &&
      multiCartItem.status === 'available',
    )
    .map((multiCartItem: App.MultiCartItem) => multiCartItem.item)
}

// Checks subscription in snapshot OR LuxPlus benefits feature flag in Redux
export function getHasLuxPlusOrSubscriptionInMultiCart(
  snapshot: MultiCartSnapshot,
  state: App.State,
): boolean {
  const allItems = selectItemsInActiveCart(snapshot)
  const subscriptionItem = allItems.find(
    (multiCartItem: App.MultiCartItem) =>
      isLuxPlusSubscriptionItem(multiCartItem.item) &&
      multiCartItem.state === 'active',
  )
  const canRedeemLuxPlusBenefits = getCanRedeemLuxPlusBenefits(state)
  return !!(subscriptionItem || canRedeemLuxPlusBenefits)
}

// Determines LuxPlus membership status for analytics contexts
// Uses multi-cart if snapshot exists and isMultiCart is true, otherwise uses checkout cart
export function getLuxPlusMemberPriceApplies(
  state: App.State,
  isMultiCart?: boolean,
): boolean {
  const multiCartActorRef = getCartMachineActor()
  const snapshot = multiCartActorRef?.getSnapshot()
  if (snapshot && isMultiCart) {
    return getHasLuxPlusOrSubscriptionInMultiCart(snapshot, state)
  }
  return getHasLuxPlusOrSubscriptionInCart(state)
}

// Returns true if any hotel/bedbank item has LuxPlus member prices, inclusions, or access
export function shouldWaiveLuxPlusJoiningFee(
  activeItems: Array<App.Checkout.AnyItem>,
  cartOffers: Record<string, App.AnyOffer>,
): boolean {
  const hotelItems = activeItems.filter(item =>
    isLEHotelItem(item) || isBedbankItem(item),
  )

  for (const item of hotelItems) {
    const offer = cartOffers[item.offerId]
    if (offer && (offer.luxPlus.hasMemberPrices || offer.luxPlus.hasMemberInclusions || offer.luxPlus.access)) {
      return true
    }
  }

  return false
}
